import Phaser from "phaser";
import { getStateCallbacks, type Room } from "colyseus.js";
import {
  type WorldState,
  type Player,
  type Npc,
  type ResourceNode,
  type Structure,
  tileToScreen,
  screenToTile,
  isoDepth,
  dist,
  darkness,
  isNight,
  NIGHT_START,
  NIGHT_END,
  BUILD_RANGE,
  WORLD_W,
  WORLD_H,
  RESOURCES,
  ResourceKind,
  ITEMS,
  type ItemId,
  STRUCTURES,
  RECIPES_BY_ID,
  StructureKind,
  BIOMES,
  Biome,
  biomeAt,
  ClientMessage,
} from "@ember/shared";
import { generateTextures } from "../game/textures.js";
import { audio } from "../game/audio.js";
import type { HUD } from "../ui/hud.js";

const LOOK_EMOJI: Record<string, string> = {
  ranger: "🧝",
  warden: "🦸",
  mystic: "🧙",
};
const NPC_EMOJI: Record<string, string> = {
  deer: "🦌",
  wolf: "🐺",
  wraith: "👻",
  elder: "🧓",
  wraithking: "👹",
};

interface Ent {
  container: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Text | Phaser.GameObjects.Image;
  rx: number;
  ry: number;
  label?: Phaser.GameObjects.Text;
  hpbar?: Phaser.GameObjects.Graphics;
  ring?: Phaser.GameObjects.Graphics;
  glow?: Phaser.GameObjects.Image;
  particles?: Phaser.GameObjects.Particles.ParticleEmitter;
  lastHp?: number;
  /** per-entity animation phase so bobs/sways don't move in lockstep */
  phase: number;
  moving?: boolean;
  /** "tamed" heart marker (animals with an owner) */
  heart?: Phaser.GameObjects.Text;
}

/** Deterministic per-key phase offset for ambient animation. */
function phaseOf(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return (h % 628) / 100; // 0..2π
}

/** Scale a 0xRRGGBB tint's brightness by f (clamped per channel). */
function scaleTint(tint: number, f: number): number {
  const r = Math.min(255, Math.round(((tint >> 16) & 0xff) * f));
  const g = Math.min(255, Math.round(((tint >> 8) & 0xff) * f));
  const b = Math.min(255, Math.round((tint & 0xff) * f));
  return (r << 16) | (g << 8) | b;
}

export class WorldScene extends Phaser.Scene {
  private room!: Room<WorldState>;
  private hud!: HUD;
  private sessionId = "";
  private myCharacterId = "";

  private groundLayer!: Phaser.GameObjects.Container;
  private lightLayer!: Phaser.GameObjects.Container;
  private darkness!: Phaser.GameObjects.Rectangle;
  private duskTint!: Phaser.GameObjects.Rectangle;
  private vignette!: Phaser.GameObjects.Image;
  private fireflies: Phaser.GameObjects.Image[] = [];
  private clouds: { img: Phaser.GameObjects.Image; ox: number; oy: number; sp: number; sc: number; a: number }[] = [];
  private targetZoom = 1;
  // a tile plus any decor (tufts/pebbles) spawned with it, recycled together
  private tiles = new Map<string, Phaser.GameObjects.Image[]>();

  private players = new Map<string, Ent>();
  private npcs = new Map<string, Ent>();
  private resources = new Map<string, Ent>();
  private structures = new Map<string, Ent>();

  private keys!: Record<string, Phaser.Input.Keyboard.Key>;
  private typingLast = false;
  private lastSent = { dx: 0, dy: 0, t: 0 };
  private placingGhost?: Phaser.GameObjects.Text | Phaser.GameObjects.Image;
  private ghostKind = "";
  private placeMark?: Phaser.GameObjects.Image;
  private lastBounds = "";
  private redFlash!: Phaser.GameObjects.Rectangle;
  private wasNight = false;
  private lastGatherXp = -1;

  // proximity audio: ids currently within earshot, and a throttle for scans
  private nearIds = new Set<string>();
  private proximityPrimed = false;
  private lastProximityScan = 0;

  constructor() {
    super("world");
  }

  init(data: { room: Room<WorldState>; hud: HUD }) {
    this.room = data.room;
    this.hud = data.hud;
    this.sessionId = data.room.sessionId;
  }

  preload() {
    generateTextures(this);
  }

  create() {
    this.cameras.main.setBackgroundColor("#11101a");
    this.cameras.main.setZoom(1);

    this.groundLayer = this.add.container(0, 0).setDepth(-1);
    this.lightLayer = this.add.container(0, 0).setDepth(900001);

    // Screen-fixed overlays. Centered with origin 0.5 and sized by 1/zoom in
    // fitOverlays(), because camera zoom scales scrollFactor-0 objects too.
    this.darkness = this.add
      .rectangle(0, 0, 10, 10, 0x080814)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(900000)
      .setAlpha(0);

    // warm color grading at dawn/dusk
    this.duskTint = this.add
      .rectangle(0, 0, 10, 10, 0xff7a30)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(899999)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0);

    // cinematic edge vignette (always on, very subtle)
    this.vignette = this.add
      .image(0, 0, "vignette")
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(940000)
      .setAlpha(0.8);

    // red damage vignette (flashed when the local player is hurt)
    this.redFlash = this.add
      .rectangle(0, 0, 10, 10, 0xc01030)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(950001)
      .setAlpha(0);

    // fireflies — a small pool of drifting glow dots, animated by hand in
    // update() (sine orbits around the player). Visible on calm nights only.
    const FIREFLY_TINTS = [0xb8ff7a, 0xd6ff9e, 0x8fe06a];
    for (let i = 0; i < 26; i++) {
      const fly = this.add
        .image(0, 0, "spark")
        .setBlendMode(Phaser.BlendModes.ADD)
        .setTint(FIREFLY_TINTS[i % FIREFLY_TINTS.length])
        .setAlpha(0);
      this.lightLayer.add(fly);
      this.fireflies.push(fly);
    }

    // cloud layer you rise into when zooming far out. Screen-fixed sprites,
    // counter-scaled by 1/zoom so the world shrinks beneath them — fading in
    // as the camera pulls back toward min zoom.
    for (let i = 0; i < 12; i++) {
      const img = this.add
        .image(0, 0, "cloud")
        .setScrollFactor(0)
        .setDepth(905000) // above the darkness overlay: clouds catch moonlight
        .setAlpha(0);
      this.clouds.push({
        img,
        ox: (i * 0.37 + 0.13) % 1,
        oy: (i * 0.53 + 0.07) % 1,
        sp: 0.6 + (i % 4) * 0.45,
        sc: 2.1 + (i % 3) * 1.1,
        a: 0.78 + ((i * 7) % 5) * 0.05,
      });
    }

    this.fitOverlays();
    this.scale.on("resize", () => this.fitOverlays());

    // unlock audio on the first interaction (browser gesture requirement)
    const unlock = () => audio.resume();
    this.input.once("pointerdown", unlock);
    this.input.keyboard!.once("keydown", unlock);

    this.keys = this.input.keyboard!.addKeys(
      "W,A,S,D,UP,LEFT,DOWN,RIGHT",
    ) as Record<string, Phaser.Input.Keyboard.Key>;

    this.input.mouse?.disableContextMenu();
    this.input.on("pointerdown", (p: Phaser.Input.Pointer) => this.onClick(p));
    this.input.on("pointermove", (p: Phaser.Input.Pointer) => this.onPointerMove(p));
    this.input.keyboard!.on("keydown-ESC", () => this.cancelPlacing());

    // mouse-wheel zoom (smoothed toward targetZoom in update)
    this.input.on(
      "wheel",
      (_p: Phaser.Input.Pointer, _o: unknown[], _dx: number, dy: number) => {
        const factor = dy > 0 ? 0.85 : 1.18;
        this.targetZoom = Phaser.Math.Clamp(this.targetZoom * factor, 0.6, 2.2);
      },
    );

    this.bindState();
    this.bindMessages();
  }

  /** Keep screen-fixed overlays covering the viewport at any zoom level.
   *  scrollFactor(0) objects are still scaled by camera zoom around the
   *  viewport center, so we counter-scale: center them and size by 1/zoom. */
  private fitOverlays() {
    const w = this.scale.width;
    const h = this.scale.height;
    const z = this.cameras.main.zoom;
    const cx = w / 2;
    const cy = h / 2;
    const ow = w / z + 4;
    const oh = h / z + 4;
    this.darkness.setPosition(cx, cy).setSize(ow, oh);
    this.duskTint.setPosition(cx, cy).setSize(ow, oh);
    this.redFlash.setPosition(cx, cy).setSize(ow, oh);
    this.vignette.setPosition(cx, cy).setDisplaySize(ow, oh);
  }

  // ----------------------------------------------------- state binding
  private bindState() {
    const $ = getStateCallbacks(this.room);
    const state = this.room.state;

    $(state).players.onAdd((player: Player, key: string) => {
      this.addPlayer(player, key);
    });
    $(state).players.onRemove((_p: Player, key: string) => this.removeEnt(this.players, key));

    $(state).npcs.onAdd((npc: Npc, key: string) => this.addNpc(npc, key));
    $(state).npcs.onRemove((_n: Npc, key: string) => this.removeEnt(this.npcs, key));

    $(state).resources.onAdd((r: ResourceNode, key: string) => this.addResource(r, key));
    $(state).resources.onRemove((_r: ResourceNode, key: string) =>
      this.removeEnt(this.resources, key),
    );

    $(state).structures.onAdd((s: Structure, key: string) => this.addStructure(s, key));
    $(state).structures.onRemove((_s: Structure, key: string) =>
      this.removeEnt(this.structures, key),
    );
  }

  private bindMessages() {
    this.room.onMessage("toast", (m: { text: string; kind?: "info" | "warn" | "good" }) => {
      this.hud.toast(m.text, m.kind ?? "info");
      if (m.text.startsWith("Crafted")) audio.craftDone();
      else if (m.text.startsWith("Built")) audio.build();
    });
    this.room.onMessage("chat", (m: { from: string; text: string }) =>
      this.hud.chat(m.from, m.text),
    );
    this.room.onMessage("quest", (m) => this.hud.quest(m));
    this.room.onMessage("levelup", (m: { skill: string; level: number }) => {
      this.hud.toast(`${m.skill} reached level ${m.level}!`, "good");
      audio.levelUp();
    });
    this.room.onMessage("died", () => {
      this.hud.toast("You have fallen…", "warn");
      audio.death();
    });
    this.room.onMessage("welcome", (m: { characterId?: string }) => {
      this.hud.system("You awaken by the embers.");
      if (m?.characterId) {
        this.hud.setCharacterId(m.characterId);
        this.myCharacterId = m.characterId;
      }
    });
    // arrow projectile animation
    this.room.onMessage("shot", (m: { fx: number; fy: number; tx: number; ty: number }) => {
      const from = tileToScreen(m.fx, m.fy);
      const to = tileToScreen(m.tx, m.ty);
      const arrow = this.add
        .image(from.x, from.y - 16, "spark")
        .setScale(1)
        .setTint(0xffe9b0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(960000);
      this.tweens.add({
        targets: arrow,
        x: to.x,
        y: to.y - 10,
        duration: 150,
        ease: "Linear",
        onComplete: () => {
          arrow.destroy();
          audio.hit();
        },
      });
    });
  }

  // ----------------------------------------------------- entity factories
  private mkContainer(x: number, y: number): Phaser.GameObjects.Container {
    return this.add.container(x, y);
  }

  private addPlayer(p: Player, key: string) {
    const { x, y } = tileToScreen(p.x, p.y);
    const c = this.mkContainer(x, y);
    const shadow = this.add.image(0, 0, "shadow").setOrigin(0.5, 0.5);
    const body = this.add
      .text(0, -2, LOOK_EMOJI[p.look] ?? "🧝", { fontSize: "30px" })
      .setOrigin(0.5, 1);
    const label = this.add
      .text(0, -36, p.name, {
        fontSize: "12px",
        color: key === this.sessionId ? "#ffb066" : "#c9c2d8",
        fontFamily: "Trebuchet MS, sans-serif",
        stroke: "#000000",
        strokeThickness: 3,
      })
      .setOrigin(0.5, 1);
    const hpbar = this.add.graphics();
    const ring = this.add.graphics();
    // ring last → renders on top of the name label
    c.add([shadow, body, hpbar, label, ring]);
    this.players.set(key, { container: c, body, label, hpbar, ring, rx: p.x, ry: p.y, lastHp: p.hp, phase: phaseOf(key) });

    if (key === this.sessionId) {
      // personal torchlight so you can always see yourself
      const glow = this.add
        .image(x, y, "glow")
        .setBlendMode(Phaser.BlendModes.ADD)
        .setScale(0.9)
        .setTint(0xffd9a0)
        .setAlpha(0.5);
      this.lightLayer.add(glow);
      this.players.get(key)!.glow = glow;
    }
  }

  private addNpc(n: Npc, key: string) {
    const { x, y } = tileToScreen(n.x, n.y);
    const c = this.mkContainer(x, y);
    const boss = n.kind === "wraithking";
    const fontSize = boss ? "46px" : n.kind === "elder" ? "30px" : "26px";
    const shadow = this.add.image(0, 0, "shadow").setOrigin(0.5, 0.5).setScale(boss ? 1.5 : 0.9);
    const body = this.add.text(0, -2, NPC_EMOJI[n.kind] ?? "❔", { fontSize }).setOrigin(0.5, 1);
    if (n.kind === "wraith") body.setAlpha(0.8);
    const hpbar = this.add.graphics();
    c.add([shadow, body, hpbar]);
    if (n.kind === "elder") {
      c.add(
        this.add
          .text(0, -34, "Elder ✦", { fontSize: "11px", color: "#ffb066", stroke: "#000", strokeThickness: 3 })
          .setOrigin(0.5, 1),
      );
    } else if (boss) {
      c.add(
        this.add
          .text(0, -52, "👑 Wraith King", {
            fontSize: "13px",
            color: "#ff6b7d",
            fontStyle: "bold",
            stroke: "#000",
            strokeThickness: 4,
          })
          .setOrigin(0.5, 1),
      );
    }
    this.npcs.set(key, { container: c, body, hpbar, rx: n.x, ry: n.y, lastHp: n.hp, phase: phaseOf(key) });
  }

  private addResource(r: ResourceNode, key: string) {
    const { x, y } = tileToScreen(r.x, r.y);
    const c = this.mkContainer(x, y);
    const def = RESOURCES[r.kind as ResourceKind];
    const body = this.add
      .text(0, -2, def?.icon ?? "🌿", { fontSize: "26px" })
      .setOrigin(0.5, 1);
    c.add(body);
    this.resources.set(key, { container: c, body, rx: r.x, ry: r.y, phase: phaseOf(key) });
  }

  private addStructure(s: Structure, key: string) {
    const { x, y } = tileToScreen(s.x, s.y);
    const c = this.mkContainer(x, y);
    const def = STRUCTURES[s.kind as StructureKind];
    // walls render as solid iso blocks that sit flush on their tile; other
    // structures keep their emoji look for now
    const body =
      s.kind === StructureKind.Wall
        ? this.add.image(0, 16, "wallBlock").setOrigin(0.5, 1).setTint(0xb98a55)
        : this.add.text(0, -2, def?.icon ?? "📦", { fontSize: "28px" }).setOrigin(0.5, 1);
    const hpbar = this.add.graphics();
    c.add([body, hpbar]);
    const ent: Ent = { container: c, body, hpbar, rx: s.x, ry: s.y, lastHp: s.hp, phase: phaseOf(key) };
    if (def?.warmthRadius) {
      const glow = this.add
        .image(x, y, "glow")
        .setBlendMode(Phaser.BlendModes.ADD)
        .setScale(def.warmthRadius / 4)
        .setTint(s.kind === StructureKind.Campfire ? 0xff9a3d : 0xffd9a0)
        .setAlpha(0.6);
      this.lightLayer.add(glow);
      ent.glow = glow;
      if (s.kind === StructureKind.Campfire) {
        ent.particles = this.add.particles(x, y - 10, "spark", {
          speed: { min: 10, max: 35 },
          angle: { min: 255, max: 285 },
          scale: { start: 0.9, end: 0 },
          alpha: { start: 0.85, end: 0 },
          lifespan: { min: 500, max: 900 },
          frequency: 80,
          gravityY: -18,
          tint: [0xff4400, 0xff8800, 0xffcc44, 0xffee88],
          blendMode: Phaser.BlendModes.ADD,
          quantity: 1,
        });
        this.lightLayer.add(ent.particles);
      }
    }
    this.structures.set(key, ent);
  }

  private removeEnt(map: Map<string, Ent>, key: string) {
    const e = map.get(key);
    if (!e) return;
    e.glow?.destroy();
    e.particles?.destroy();
    e.container.destroy();
    map.delete(key);
  }

  // ----------------------------------------------------------- input
  private cancelPlacing() {
    if (this.hud.placingRecipe || this.hud.placingFood) {
      this.hud.clearPlacing();
      this.clearGhost();
    }
  }

  private onClick(p: Phaser.Input.Pointer) {
    if (this.hud.isChatOpen) return;
    // right-click cancels build placement
    if (p.rightButtonDown()) {
      this.cancelPlacing();
      return;
    }
    const wp = this.cameras.main.getWorldPoint(p.x, p.y);
    const { tx, ty } = screenToTile(wp.x, wp.y);

    if (this.hud.placingRecipe || this.hud.placingFood) {
      const sx = Math.round(tx);
      const sy = Math.round(ty);
      const reason = this.placeBlockReason(sx, sy);
      if (reason) {
        // stay in placing mode so the player can pick a better tile
        this.hud.toast(reason, "warn");
        return;
      }
      if (this.hud.placingFood) {
        this.room.send(ClientMessage.PlaceFood, { item: this.hud.placingFood, tx: sx, ty: sy });
      } else {
        this.room.send(ClientMessage.Place, { recipeId: this.hud.placingRecipe, tx: sx, ty: sy });
      }
      this.hud.clearPlacing();
      this.clearGhost();
      return;
    }

    // find nearest interactable to the click point
    let best: { kind: string; id: string; d: number } | null = null;
    const consider = (id: string, ex: number, ey: number, kind: string) => {
      const d = dist(tx, ty, ex, ey);
      if (d < 1.3 && (!best || d < best.d)) best = { kind, id, d };
    };
    this.room.state.resources.forEach((r, id) => consider(id, r.x, r.y, "resource"));
    this.room.state.npcs.forEach((n, id) => {
      if (n.kind === "deer" || n.kind === "wraith") consider(id, n.x, n.y, "attack");
      else if (n.kind === "elder") consider(id, n.x, n.y, "elder");
    });
    if (!best) return;
    const target = best as { kind: string; id: string; d: number };
    if (target.kind === "resource") this.room.send(ClientMessage.Gather, { resourceId: target.id });
    else if (target.kind === "attack") this.room.send(ClientMessage.Attack, { targetId: target.id });
    else if (target.kind === "elder") this.room.send(ClientMessage.Interact, { npcId: target.id });
  }

  /** Mirror of the server's placement rules (which stay authoritative).
   *  Returns a human reason the tile is invalid, or null if it looks fine. */
  private placeBlockReason(tx: number, ty: number): string | null {
    const me = this.room.state.players.get(this.sessionId);
    if (!me) return "Not ready yet.";
    if (tx < 0 || ty < 0 || tx >= WORLD_W || ty >= WORLD_H) return "Out of bounds.";
    if (dist(me.x, me.y, tx, ty) > BUILD_RANGE) return "Too far away to build there.";
    let blocked = false;
    this.room.state.structures.forEach((s: Structure) => {
      if (dist(s.x, s.y, tx, ty) < 0.8) blocked = true;
    });
    return blocked ? "Something is already there." : null;
  }

  private onPointerMove(p: Phaser.Input.Pointer) {
    if (!this.hud.placingRecipe && !this.hud.placingFood) {
      this.clearGhost();
      return;
    }
    const wp = this.cameras.main.getWorldPoint(p.x, p.y);
    const { tx, ty } = screenToTile(wp.x, wp.y);
    const sx = Math.round(tx);
    const sy = Math.round(ty);
    const snap = tileToScreen(sx, sy);
    // ghost shows what you're actually placing — structure or bait
    const food = this.hud.placingFood;
    const kind = food ? "" : (RECIPES_BY_ID[this.hud.placingRecipe!]?.outputStructure ?? "");
    const ghostKey = food ? `food:${food}` : kind;
    if (ghostKey !== this.ghostKind) {
      this.placingGhost?.destroy();
      this.placingGhost = undefined;
      this.ghostKind = ghostKey;
    }
    if (!this.placingGhost) {
      const icon = food
        ? (ITEMS[food as ItemId]?.icon ?? "🍖")
        : kind
          ? STRUCTURES[kind as StructureKind].icon
          : "🔥";
      this.placingGhost =
        kind === StructureKind.Wall
          ? this.add.image(0, 0, "wallBlock").setOrigin(0.5, 1).setTint(0xb98a55).setDepth(950000)
          : this.add
              .text(0, 0, icon, { fontSize: "28px" })
              .setOrigin(0.5, 1)
              .setDepth(950000);
    }
    // tile highlight: green = placeable, red = blocked/too far
    if (!this.placeMark) {
      this.placeMark = this.add.image(0, 0, "tile_outline").setDepth(949999);
    }
    const ok = this.placeBlockReason(sx, sy) === null;
    this.placeMark
      .setPosition(snap.x, snap.y)
      .setTint(ok ? 0x6fd08c : 0xe0556b)
      .setAlpha(0.9);
    this.placingGhost.setAlpha(ok ? 0.6 : 0.25);
    this.placingGhost.setPosition(snap.x, kind === StructureKind.Wall ? snap.y + 16 : snap.y - 2);
  }

  private clearGhost() {
    this.placingGhost?.destroy();
    this.placingGhost = undefined;
    this.ghostKind = "";
    this.placeMark?.destroy();
    this.placeMark = undefined;
  }

  private readInput() {
    if (this.hud.isChatOpen) return { dx: 0, dy: 0 };
    const k = this.keys;
    const up = k.W.isDown || k.UP.isDown;
    const down = k.S.isDown || k.DOWN.isDown;
    const left = k.A.isDown || k.LEFT.isDown;
    const right = k.D.isDown || k.RIGHT.isDown;
    // screen direction -> isometric tile-space basis
    let dx = 0;
    let dy = 0;
    if (up) {
      dx -= 1;
      dy -= 1;
    }
    if (down) {
      dx += 1;
      dy += 1;
    }
    if (left) {
      dx -= 1;
      dy += 1;
    }
    if (right) {
      dx += 1;
      dy -= 1;
    }
    const m = Math.hypot(dx, dy);
    if (m > 0) {
      dx /= m;
      dy /= m;
    }
    return { dx, dy };
  }

  // ----------------------------------------------------------- update
  update(time: number, delta: number) {
    const state = this.room.state;
    // With @view() filtering, the collections are not instantiated client-side
    // until the first filtered patch arrives. Bail safely until then so we never
    // throw inside Phaser's loop (which would stall rendering).
    if (!state || !state.players || !state.npcs || !state.resources || !state.structures) {
      return;
    }
    const me = state.players.get(this.sessionId);

    // While a HUD text field is focused, hand the keyboard to the DOM so typed
    // letters reach the input (Phaser captures WASD/arrows with preventDefault,
    // which otherwise eats the keystrokes AND moves the character). Reset keys
    // on each transition so a key held when the field opened doesn't stick down.
    const typing = this.hud.isTyping;
    if (typing !== this.typingLast) {
      this.typingLast = typing;
      const kb = this.input.keyboard;
      if (kb) {
        // enabled=false stops movement; but the WASD/arrow *capture*
        // (preventDefault) lives on the keyboard manager and must be lifted
        // separately, or those letters never reach the focused input.
        kb.enabled = !typing;
        if (typing) kb.removeCapture("W,A,S,D,UP,LEFT,DOWN,RIGHT");
        else kb.addCapture("W,A,S,D,UP,LEFT,DOWN,RIGHT");
        kb.resetKeys();
      }
    }

    // input → server (throttled, on change)
    const { dx, dy } = this.readInput();
    if (
      Math.abs(dx - this.lastSent.dx) > 0.01 ||
      Math.abs(dy - this.lastSent.dy) > 0.01 ||
      time - this.lastSent.t > 120
    ) {
      this.room.send(ClientMessage.Move, { dx, dy });
      this.lastSent = { dx, dy, t: time };
    }

    const lerp = 1 - Math.pow(0.001, delta / 1000); // frame-rate independent smoothing

    this.smooth(this.players, this.room.state.players, lerp);
    this.smooth(this.npcs, this.room.state.npcs, lerp);
    this.smoothStatic(this.resources, this.room.state.resources);
    this.smoothStatic(this.structures, this.room.state.structures);

    this.updatePlayerVisuals();
    this.updateNpcVisuals();
    this.updateResourceVisuals();
    this.updateStructureVisuals();

    // camera follow local render position + smooth wheel zoom
    const cam = this.cameras.main;
    if (Math.abs(cam.zoom - this.targetZoom) > 0.0005) {
      cam.setZoom(cam.zoom + (this.targetZoom - cam.zoom) * Math.min(1, delta / 90));
      this.fitOverlays();
    }
    if (me) {
      const self = this.players.get(this.sessionId);
      if (self) {
        const { x, y } = tileToScreen(self.rx, self.ry);
        cam.centerOn(x, y - 8);
      }
    }

    this.updateGround();
    this.animateAmbient(time);
    this.updateClouds(time);

    // lighting — Blood Moon nights are darker and tinted blood-red
    const t = this.room.state.timeOfDay;
    const d = darkness(t);
    const blood = this.room.state.bloodMoon;
    this.darkness.setFillStyle(blood ? 0x2a0410 : 0x080814);
    this.darkness.setAlpha(d * (blood ? 0.88 : 0.82));
    this.hud.setBloodMoon(blood);

    // warm additive grading around dawn and dusk
    const edgeDist = Math.min(Math.abs(t - NIGHT_START), Math.abs(t - NIGHT_END));
    const goldenHour = Math.max(0, 1 - edgeDist / 0.05);
    this.duskTint.setAlpha(goldenHour * 0.12);

    // fireflies drift on calm (non-blood-moon) nights around the player —
    // sine orbits with per-fly speed/radius, alpha pulsing in and out
    const flyVis = blood ? 0 : Math.max(0, (d - 0.35) / 0.65);
    if (me) {
      const self = this.players.get(this.sessionId);
      if (self && (flyVis > 0 || this.fireflies[0]?.alpha)) {
        const { x: px, y: py } = tileToScreen(self.rx, self.ry);
        for (let i = 0; i < this.fireflies.length; i++) {
          const fly = this.fireflies[i];
          const ox = Math.sin(time * 0.00011 * (1 + (i % 5)) + i * 2.4) * (300 + (i % 7) * 45);
          const oy = Math.cos(time * 0.00009 * (1 + (i % 4)) + i * 1.7) * (180 + (i % 5) * 35);
          const pulse = 0.5 + 0.5 * Math.sin(time * 0.0035 + i * 2.1);
          fly.setPosition(px + ox, py + oy - 14);
          fly.setAlpha(flyVis * (0.2 + 0.7 * pulse));
          fly.setScale(0.45 + 0.25 * pulse);
        }
      }
    }

    // make personal/fire glow stronger at night, with a living flicker
    for (const ent of this.players.values())
      if (ent.glow) {
        const flicker = 1 + Math.sin(time * 0.011 + ent.phase) * 0.06 + Math.sin(time * 0.029 + ent.phase * 2) * 0.04;
        ent.glow.setAlpha((0.15 + d * 0.55) * flicker);
      }
    for (const ent of this.structures.values())
      if (ent.glow) {
        const flicker = 1 + Math.sin(time * 0.013 + ent.phase) * 0.08 + Math.sin(time * 0.031 + ent.phase * 2) * 0.05;
        ent.glow.setAlpha((0.35 + d * 0.5) * flicker);
      }

    // audio: ambient day/night + transition cues
    audio.setNight(d);
    audio.setBloodMoon(blood);
    const night = isNight(this.room.state.timeOfDay);
    if (night && !this.wasNight) audio.nightfall();
    else if (!night && this.wasNight) audio.dawn();
    this.wasNight = night;

    // HUD + interaction prompt
    if (me) {
      audio.setNearFire(this.nearFire(me.x, me.y));
      this.updateProximityAudio(me, time);
      // chop/mine cue when a gather completes (gathering xp ticks up)
      if (this.lastGatherXp >= 0 && me.skills.gatheringXp > this.lastGatherXp) audio.chop();
      this.lastGatherXp = me.skills.gatheringXp;

      this.hud.update(me, this.room.state);
      this.updateInteractPrompt(me);
    }
  }

  private nearFire(x: number, y: number): boolean {
    let near = false;
    this.room.state.structures.forEach((s: Structure) => {
      const def = STRUCTURES[s.kind as StructureKind];
      if (def?.warmthRadius && dist(x, y, s.x, s.y) <= def.warmthRadius) near = true;
    });
    return near;
  }

  /**
   * Drive the proximity beds + entry cues: scale a warm/menacing bed with how
   * many friendly/hostile entities are within earshot, and play a one-shot when
   * a *new* one enters. Throttled to ~2.5 Hz, and primed silently on the first
   * scan so spawning next to existing entities doesn't fire a burst of cues.
   */
  private updateProximityAudio(me: Player, time: number) {
    if (time - this.lastProximityScan < 400) return;
    this.lastProximityScan = time;

    const R = 6; // tiles — closer than the AOI cull, so cues feel "near"
    let friendly = 0;
    let hostile = 0;
    let newFriendly = false;
    let newHostile = false;
    const now = new Set<string>();

    this.room.state.players.forEach((p: Player, key: string) => {
      if (key === this.sessionId || p.dead) return;
      if (dist(me.x, me.y, p.x, p.y) > R) return;
      friendly++;
      const id = `p:${key}`;
      now.add(id);
      if (!this.nearIds.has(id)) newFriendly = true;
    });

    this.room.state.npcs.forEach((n: Npc, key: string) => {
      if (dist(me.x, me.y, n.x, n.y) > R) return;
      const id = `n:${key}`;
      now.add(id);
      if (n.hostile) {
        hostile++;
        if (!this.nearIds.has(id)) newHostile = true;
      } else {
        friendly++;
        if (!this.nearIds.has(id)) newFriendly = true;
      }
    });

    this.nearIds = now;
    audio.setPresence(friendly, hostile);

    if (this.proximityPrimed) {
      // danger takes priority; at most one cue per scan keeps it from spamming
      if (newHostile) audio.nearHostile();
      else if (newFriendly) audio.nearFriendly();
    } else {
      this.proximityPrimed = true;
    }
  }

  /** Clouds fade in as the camera pulls back (zoom < ~0.85) — flying up into
   *  the cloud deck. They drift with time and parallax against camera scroll. */
  private updateClouds(time: number) {
    const cam = this.cameras.main;
    const z = cam.zoom;
    const deck = Phaser.Math.Clamp((0.85 - z) / 0.25, 0, 1);
    if (deck <= 0.01) {
      for (const c of this.clouds) c.img.setVisible(false);
      return;
    }
    const w = this.scale.width;
    const h = this.scale.height;
    const spanX = w + 900;
    const spanY = h + 500;
    for (const c of this.clouds) {
      c.img.setVisible(true);
      // desired position in screen space: base spot + slow drift − parallax
      let sx = (c.ox * spanX + time * 0.014 * c.sp - cam.scrollX * 0.18) % spanX;
      let sy = (c.oy * spanY + time * 0.003 * c.sp - cam.scrollY * 0.18) % spanY;
      if (sx < 0) sx += spanX;
      if (sy < 0) sy += spanY;
      sx -= 450;
      sy -= 250;
      // counter the camera-zoom scaling of scrollFactor-0 objects, so clouds
      // keep their screen size while the world shrinks underneath
      c.img.setPosition(w / 2 + (sx - w / 2) / z, h / 2 + (sy - h / 2) / z);
      c.img.setScale(c.sc / z);
      c.img.setAlpha(deck * c.a);
    }
  }

  /** Ambient life: walk-bobs, ghost floats, tree sway. Cheap sin() per entity. */
  private animateAmbient(time: number) {
    // players hop while moving, settle when idle
    for (const e of this.players.values()) {
      const bob = e.moving ? -Math.abs(Math.sin(time * 0.012 + e.phase)) * 4 : 0;
      e.body.setY(-2 + bob);
    }
    // npcs: wraiths float and shimmer, deer bob while moving
    this.room.state.npcs.forEach((n: Npc, key: string) => {
      const e = this.npcs.get(key);
      if (!e) return;
      if (n.kind === "wraith" || n.kind === "wraithking") {
        const amp = n.kind === "wraithking" ? 4 : 2.5;
        e.body.setY(-4 + Math.sin(time * 0.0035 + e.phase) * amp);
        e.body.setAlpha(0.72 + Math.sin(time * 0.005 + e.phase * 2) * 0.13);
      } else if (e.moving) {
        e.body.setY(-2 - Math.abs(Math.sin(time * 0.011 + e.phase)) * 3);
      } else {
        e.body.setY(-2);
      }
    });
    // trees and bushes sway in the wind; rocks and veins stand still
    this.room.state.resources.forEach((r: ResourceNode, key: string) => {
      const e = this.resources.get(key);
      if (!e) return;
      if (r.kind === ResourceKind.Tree)
        e.body.setAngle(Math.sin(time * 0.0012 + e.phase) * 2.4);
      else if (r.kind === ResourceKind.Bush)
        e.body.setAngle(Math.sin(time * 0.0016 + e.phase) * 1.5);
      else if (r.kind === ResourceKind.Grass)
        e.body.setAngle(Math.sin(time * 0.002 + e.phase) * 5);
    });
  }

  /** Render only the iso ground tiles currently visible; recycle on camera move. */
  private updateGround() {
    const view = this.cameras.main.worldView;
    const pad = 2;
    const corners = [
      screenToTile(view.left, view.top),
      screenToTile(view.right, view.top),
      screenToTile(view.left, view.bottom),
      screenToTile(view.right, view.bottom),
    ];
    let minTx = Infinity,
      maxTx = -Infinity,
      minTy = Infinity,
      maxTy = -Infinity;
    for (const c of corners) {
      minTx = Math.min(minTx, c.tx);
      maxTx = Math.max(maxTx, c.tx);
      minTy = Math.min(minTy, c.ty);
      maxTy = Math.max(maxTy, c.ty);
    }
    const x0 = Math.max(0, Math.floor(minTx) - pad);
    const x1 = Math.min(WORLD_W - 1, Math.ceil(maxTx) + pad);
    const y0 = Math.max(0, Math.floor(minTy) - pad);
    const y1 = Math.min(WORLD_H - 1, Math.ceil(maxTy) + pad);

    const sig = `${x0},${x1},${y0},${y1}`;
    if (sig === this.lastBounds) return; // bounds unchanged — nothing to do
    this.lastBounds = sig;

    const needed = new Set<string>();
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const key = `${tx},${ty}`;
        needed.add(key);
        if (!this.tiles.has(key)) {
          const { x, y } = tileToScreen(tx, ty);
          const biome = biomeAt(tx, ty);
          const baseTint = BIOMES[biome].tint;
          // deterministic per-tile hash drives variant, brightness and decor,
          // so every client renders the same ground
          const h = ((tx * 73856093) ^ (ty * 19349663)) >>> 0;
          const tex = ["tile", "tileV1", "tileV2"][h % 3];
          const tint = scaleTint(baseTint, 0.93 + ((h >>> 3) % 14) / 100);
          const img = this.add.image(x, y, tex).setOrigin(0.5, 0.5).setTint(tint);
          this.groundLayer.add(img);
          const group = [img];
          // sprinkle decor on ~1 in 5 tiles: pebbles on highland, tufts elsewhere
          if (h % 5 === 0) {
            const dx = ((h >>> 5) % 25) - 12;
            const dy = ((h >>> 9) % 9) - 4;
            const highland = biome === Biome.Highland;
            const decor = this.add
              .image(x + dx, y + dy, highland ? "pebble" : "tuft")
              .setOrigin(0.5, 1)
              .setTint(scaleTint(baseTint, highland ? 1.15 : 1.45));
            this.groundLayer.add(decor);
            group.push(decor);
          }
          this.tiles.set(key, group);
        }
      }
    }
    for (const [key, group] of this.tiles) {
      if (!needed.has(key)) {
        for (const img of group) img.destroy();
        this.tiles.delete(key);
      }
    }
  }

  private smooth(
    sprites: Map<string, Ent>,
    state: { forEach(cb: (v: { x: number; y: number }, k: string) => void): void },
    lerp: number,
  ) {
    state.forEach((s, key) => {
      const e = sprites.get(key);
      if (!e) return;
      e.moving = Math.abs(s.x - e.rx) + Math.abs(s.y - e.ry) > 0.03;
      e.rx += (s.x - e.rx) * lerp;
      e.ry += (s.y - e.ry) * lerp;
      const { x, y } = tileToScreen(e.rx, e.ry);
      e.container.setPosition(x, y);
      e.container.setDepth(isoDepth(e.rx, e.ry) + 1);
      if (e.glow) e.glow.setPosition(x, y);
      if (e.particles) e.particles.setPosition(x, y - 10);
    });
  }

  private smoothStatic(
    sprites: Map<string, Ent>,
    state: { forEach(cb: (v: { x: number; y: number }, k: string) => void): void },
  ) {
    state.forEach((s, key) => {
      const e = sprites.get(key);
      if (!e) return;
      const { x, y } = tileToScreen(s.x, s.y);
      e.container.setPosition(x, y);
      e.container.setDepth(isoDepth(s.x, s.y) + 1);
      if (e.glow) e.glow.setPosition(x, y);
      if (e.particles) e.particles.setPosition(x, y - 10);
    });
  }

  private updatePlayerVisuals() {
    this.room.state.players.forEach((p: Player, key: string) => {
      const e = this.players.get(key);
      if (!e || !e.hpbar) return;
      e.body.setAlpha(p.dead ? 0.35 : 1);
      this.drawHpBar(e.hpbar, p.hp, 100, 28);
      if (e.ring) this.drawRing(e.ring, p.action, p.actionPct);

      // damage feedback — only for discrete hits. The survival meters drain HP
      // as a float (~0.07/tick when freezing/starving); reacting to that would
      // strobe the red flash, spam decimal popups, and ratchet the punch scale.
      if (e.lastHp !== undefined && !p.dead) {
        const dmg = e.lastHp - p.hp;
        if (dmg >= 1) {
          this.floatDamage(e, Math.round(dmg), "#ff6b7d");
          this.punch(e.body);
          if (key === this.sessionId) {
            this.redFlash.setAlpha(Math.min(0.5, 0.12 + dmg * 0.04));
            this.tweens.add({ targets: this.redFlash, alpha: 0, duration: 450 });
            audio.hurt();
          }
        }
      }
      e.lastHp = p.hp;
    });
  }

  private updateNpcVisuals() {
    this.room.state.npcs.forEach((n: Npc, key: string) => {
      const e = this.npcs.get(key);
      if (!e || !e.hpbar) return;
      if (n.kind === "elder") return;
      // tamed animals wear a heart — gold for yours, pink for others'
      if (n.ownerId && !e.heart) {
        e.heart = this.add
          .text(0, -40, "❤", { fontSize: "13px", stroke: "#000", strokeThickness: 3 })
          .setOrigin(0.5, 1);
        e.container.add(e.heart);
      }
      if (e.heart) {
        e.heart.setVisible(!!n.ownerId);
        e.heart.setColor(n.ownerId === this.myCharacterId ? "#ffb066" : "#ff8d99");
      }
      this.drawHpBar(e.hpbar, n.hp, n.maxHp, n.kind === "wraithking" ? 52 : 24);
      if (e.lastHp !== undefined && n.hp < e.lastHp) {
        this.floatDamage(e, e.lastHp - n.hp, "#ffe08a");
        this.punch(e.body);
        audio.hit();
      }
      e.lastHp = n.hp;
    });
  }

  private updateStructureVisuals() {
    this.room.state.structures.forEach((s: Structure, key: string) => {
      const e = this.structures.get(key);
      if (!e || !e.hpbar) return;
      this.drawHpBar(e.hpbar, s.hp, s.maxHp, 26);
      if (e.lastHp !== undefined && s.hp < e.lastHp) {
        this.floatDamage(e, e.lastHp - s.hp, "#ff9a3d");
        this.punch(e.body);
      }
      e.lastHp = s.hp;
    });
  }

  private drawRing(g: Phaser.GameObjects.Graphics, action: string, pct: number) {
    g.clear();
    if (!action) return;
    const cy = -66;
    const r = 15;
    // dark backing disc for contrast
    g.fillStyle(0x000000, 0.45);
    g.fillCircle(0, cy, r + 3);
    // track
    g.lineStyle(4, 0x2a2536, 1);
    g.beginPath();
    g.arc(0, cy, r, 0, Math.PI * 2);
    g.strokePath();
    // progress arc
    const col = action === "craft" ? 0xffce63 : 0x6fd08c;
    g.lineStyle(4, col, 1);
    g.beginPath();
    g.arc(0, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (pct / 100));
    g.strokePath();
  }

  private floatDamage(e: Ent, amount: number, color: string) {
    const t = this.add
      .text(e.container.x, e.container.y - 34, `-${amount}`, {
        fontSize: "16px",
        color,
        fontStyle: "bold",
        stroke: "#000000",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(960000);
    this.tweens.add({
      targets: t,
      y: t.y - 28,
      alpha: 0,
      duration: 800,
      ease: "Cubic.out",
      onComplete: () => t.destroy(),
    });
  }

  private punch(obj: Phaser.GameObjects.Text | Phaser.GameObjects.Image) {
    // All punch targets (player/npc/structure bodies) sit at base scale 1.
    // Snap back and clear any in-flight tween first, so overlapping hits (e.g.
    // several wraiths at night) can't ratchet the scale upward without bound.
    this.tweens.killTweensOf(obj);
    obj.setScale(1);
    this.tweens.add({ targets: obj, scale: 1.3, duration: 70, yoyo: true });
  }

  private updateResourceVisuals() {
    this.room.state.resources.forEach((r: ResourceNode, key: string) => {
      const e = this.resources.get(key);
      if (!e) return;
      const def = RESOURCES[r.kind as ResourceKind];
      const ratio = def ? r.amount / def.maxAmount : 1;
      e.body.setAlpha(0.35 + 0.65 * ratio);
      e.body.setScale(0.7 + 0.3 * ratio);
    });
  }

  private drawHpBar(g: Phaser.GameObjects.Graphics, hp: number, max: number, w: number) {
    g.clear();
    if (hp >= max) return;
    const ratio = Math.max(0, hp / max);
    g.fillStyle(0x000000, 0.6);
    g.fillRect(-w / 2 - 1, -41, w + 2, 5);
    g.fillStyle(ratio > 0.5 ? 0x6fd08c : ratio > 0.25 ? 0xffce63 : 0xe0556b, 1);
    g.fillRect(-w / 2, -40, w * ratio, 3);
  }

  private updateInteractPrompt(me: Player) {
    let near: string | null = null;
    this.room.state.npcs.forEach((n: Npc) => {
      if (n.kind === "elder" && dist(me.x, me.y, n.x, n.y) <= 2.5) {
        near = "Click the Elder to speak ✦";
      }
    });
    this.hud.interactPrompt(near);
  }
}
